import { motion } from "framer-motion";

import type { MarketingContent } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface ContentPanelProps {
  data: MarketingContent | null;
  loading: boolean;
  empty: boolean;
  /** Abre el riel con las slides renderizadas. Sólo hay algo que abrir cuando
   *  el formato es carrusel. */
  onVerCarrusel: (slug: string) => void;
}

/**
 * El copy generado y cómo llegó a serlo.
 *
 * El historial de intentos importa tanto como el resultado: es donde se ve qué
 * encontró el juez y si la reescritura lo arregló. Un score sin ese desglose
 * no se puede discutir.
 */
export function ContentPanel({ data, loading, empty, onVerCarrusel }: ContentPanelProps) {
  if (empty) {
    return (
      <div className="surface p-10 text-center">
        <p className="font-mono text-sm text-muted">$ elegí una plataforma</p>
      </div>
    );
  }
  if (loading || !data) {
    return <div className="surface h-64 animate-pulse" />;
  }

  return (
    <motion.div
      key={`${data.slug}-${data.platform}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="surface p-4">
        <div className="flex items-baseline justify-between gap-3">
          <p className="path-chip">{data.platform}</p>
          <span className="font-mono text-lg tabular-nums">{data.score ?? "—"}</span>
        </div>
        {/* El carrusel es el único formato que el pipeline ya renderiza, así
            que es el único donde "producir" no es una tarea pendiente sino
            algo que se puede mirar y decidir acá mismo. */}
        {data.formato === "carrusel" ? (
          <button
            type="button"
            onClick={() => onVerCarrusel(data.slug)}
            className="mt-2 w-full px-3 py-2 rounded border border-primary/30 bg-primary/5
                       font-mono text-[11px] text-primary text-left
                       hover:bg-primary/15 hover:border-primary/50 transition-colors"
          >
            🎞 ver el carrusel renderizado — aprobar o rechazar
          </button>
        ) : (
          data.needs_production && (
            <p className="mt-2 font-mono text-[11px] text-warn">
              🎬 no se publica tal cual — hay que grabarlo o renderizarlo
            </p>
          )
        )}
        <div className="mt-3 pt-3 border-t border-border/40">
          <Rendered content={data.content} platform={data.platform} />
        </div>
      </div>

      {data.attempts.length > 0 && (
        <div className="surface p-4 space-y-3">
          <p className="path-chip">intentos</p>
          {data.attempts.map((a) => (
            <div key={a.attempt} className="border-t border-border/40 pt-3 first:border-0 first:pt-0">
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-xs text-muted">#{a.attempt}</span>
                <span className="font-mono text-sm tabular-nums">{a.score}</span>
                {!a.passed_limits && (
                  <span className="font-mono text-[10px] text-danger uppercase">
                    fuera de formato
                  </span>
                )}
              </div>
              {a.factors.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {a.factors.map((f, i) => (
                    <div key={i} className="flex justify-between gap-3 text-[11px]">
                      <span className="text-muted truncate">{f.factor}</span>
                      <span className="font-mono tabular-nums shrink-0">{f.points}</span>
                    </div>
                  ))}
                </div>
              )}
              {a.gaps.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {a.gaps.map((g, i) => (
                    <li key={i} className="text-[11px] text-muted leading-relaxed">
                      <span className="text-warn">▸</span> {g.problem}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

/** Cada formato se lee distinto: un hilo no es un guion y un guion no es un post. */
function Rendered({ content, platform }: { content: MarketingContent["content"]; platform: string }) {
  if (!content) return <p className="text-sm text-muted">Sin contenido.</p>;

  if (platform === "x" && typeof content.post === "string") {
    const tweets = content.post.split("---").map((t) => t.trim()).filter(Boolean);
    return (
      <div className="space-y-2">
        {tweets.map((t, i) => (
          <div key={i} className="rounded border border-border p-2.5">
            <div className="flex justify-between font-mono text-[10px] text-muted mb-1">
              <span>{i + 1}/{tweets.length}</span>
              <span className={cn("tabular-nums", t.length > 270 && "text-danger")}>
                {t.length}
              </span>
            </div>
            <p className="text-sm whitespace-pre-wrap">{t}</p>
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(content.beats)) {
    return (
      <div className="space-y-2">
        {typeof content.hook === "string" && (
          <p className="text-sm font-medium">{content.hook}</p>
        )}
        {(content.beats as Record<string, string>[]).map((b, i) => (
          <div key={i} className="rounded border border-border p-2.5 space-y-1">
            <div className="font-mono text-[10px] text-primary">{b.seconds}</div>
            <div className="text-[13px]">{b.on_screen}</div>
            <div className="text-[13px] text-muted italic">{b.voiceover}</div>
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(content.slides)) {
    return (
      <div className="space-y-2">
        {(content.slides as Record<string, unknown>[]).map((s, i) => (
          <div key={i} className="rounded border border-border p-2.5">
            <div className="flex justify-between font-mono text-[10px] text-muted mb-1">
              <span>{String(s.type)}</span>
              <span>{i + 1}</span>
            </div>
            {!!s.title && <p className="text-[13px] font-medium">{String(s.title)}</p>}
            {!!s.text && <p className="text-[13px] whitespace-pre-wrap">{String(s.text)}</p>}
            {Array.isArray(s.items) && (s.items as string[]).length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {(s.items as string[]).map((it, j) => (
                  <li key={j} className="text-[12px] text-muted">· {it}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof content.title === "string" && typeof content.post === "string") {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium">{content.title}</p>
        {typeof content.subreddit === "string" && (
          <p className="font-mono text-[11px] text-primary">{content.subreddit}</p>
        )}
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{content.post}</p>
      </div>
    );
  }

  if (typeof content.post === "string") {
    return (
      <p className="text-sm whitespace-pre-wrap leading-relaxed">{content.post}</p>
    );
  }

  return (
    <pre className="text-[11px] font-mono text-muted overflow-x-auto">
      {JSON.stringify(content, null, 2)}
    </pre>
  );
}
